import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Users, Award, TrendingUp, Calendar, Plus, QrCode, Bell, AlertTriangle } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import { auditLogger } from '@/utils/auditLogger'
import { Badge } from '@/components/ui/badge'
import { PushNotificationManager } from '@/components/admin/PushNotificationManager'
import { SubscriptionBanner } from '@/components/subscription/SubscriptionBanner'
import { UsageLimitAlert } from '@/components/subscription/UsageLimitAlert'
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess'
import SecurityMonitoring from '@/components/admin/SecurityMonitoring'
import { FollowUpWidget } from '@/components/admin/FollowUpWidget'
import { useFollowUpNotifications } from '@/hooks/useFollowUpNotifications'
import { useNavigate } from 'react-router-dom'
import RedemptionTasksWidget from '@/components/admin/RedemptionTasksWidget'
import { SocialEngagementWidget } from '@/components/admin/SocialEngagementWidget'

interface DashboardStats {
  totalCustomers: number
  activeCustomers: number
  totalStamps: number
  stampsToday: number
}

interface RecentActivity {
  id: string
  customer_name: string
  action: string
  points: number
  timestamp: string
}

const AdminDashboard = () => {
  const { user, tenantId } = useAuth()
  const { canAddCustomers, canAddStamps, currentFeatures } = useSubscriptionAccess()
  const { totalDue: dueFollowUpsCount } = useFollowUpNotifications()
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats>({
    totalCustomers: 0,
    activeCustomers: 0,
    totalStamps: 0,
    stampsToday: 0
  })
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([])
  const [customers, setCustomers] = useState<Array<{id: string, full_name: string, email: string}>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [user, tenantId]) // Also trigger when user changes

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      let targetTenantId = tenantId
      
      // If admin has no tenant, use demo data
      if (!targetTenantId) {
        console.log('No tenant found for admin, using demo data')
        // Set demo stats for admins without tenant
        setStats({
          totalCustomers: 0,
          activeCustomers: 0,
          totalStamps: 0,
          stampsToday: 0
        })
        setRecentActivity([])
        return
      }
      
      // Fetch customer statistics
      const { data: customers, error: customersError } = await supabase
        .from('customers')
        .select('id, last_visit, full_name, email')
        .eq('tenant_id', targetTenantId)

      if (customersError) throw customersError

      // Store customers for push notifications
      const customersForPush = customers?.map(c => ({
        id: c.id,
        full_name: c.full_name,
        email: c.email
      })) || []
      setCustomers(customersForPush)

      // Fetch stamp statistics
      const { data: stamps, error: stampsError } = await supabase
        .from('stamps')
        .select('id, points_awarded, created_at')
        .eq('tenant_id', targetTenantId)

      if (stampsError) throw stampsError

      // Calculate stats
      const totalCustomers = customers?.length || 0
      const activeCustomers = customers?.filter(c => {
        if (!c.last_visit) return false
        const lastVisit = new Date(c.last_visit)
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        return lastVisit > thirtyDaysAgo
      }).length || 0

      const totalStamps = stamps?.length || 0
      const today = new Date().toDateString()
      const stampsToday = stamps?.filter(s => {
        const stampDate = new Date(s.created_at).toDateString()
        return stampDate === today
      }).length || 0

      setStats({
        totalCustomers,
        activeCustomers,
        totalStamps,
        stampsToday
      })

      // Fetch recent activity only if we have stamps
      if (totalStamps > 0) {
        const { data: recentStamps, error: recentError } = await supabase
          .from('stamps')
          .select('id, customer_id, points_awarded, created_at')
          .eq('tenant_id', targetTenantId)
          .order('created_at', { ascending: false })
          .limit(10)

        if (recentError) throw recentError

        // Get customer names separately
        const activity = await Promise.all(
          (recentStamps || []).map(async (stamp) => {
            let customerName = 'Unbekannt'
            
            if (stamp.customer_id) {
              const { data: customer } = await supabase
                .from('customers')
                .select('full_name')
                .eq('id', stamp.customer_id)
                .maybeSingle()
              
              customerName = customer?.full_name || 'Unbekannt'
            }

            return {
              id: stamp.id,
              customer_name: customerName,
              action: 'Stempel erhalten',
              points: stamp.points_awarded,
              timestamp: stamp.created_at
            }
          })
        )

        setRecentActivity(activity)
      } else {
        setRecentActivity([])
      }
      
      await auditLogger.logDataAccess('admin_dashboard', targetTenantId, 'view')
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      // Set empty stats on error
      setStats({
        totalCustomers: 0,
        activeCustomers: 0,
        totalStamps: 0,
        stampsToday: 0
      })
      setRecentActivity([])
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit', 
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getGrowthPercentage = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? '+100%' : '0%'
    const growth = ((current - previous) / previous) * 100
    return growth >= 0 ? `+${growth.toFixed(1)}%` : `${growth.toFixed(1)}%`
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Subscription Banner */}
        <SubscriptionBanner />
        
        {/* Usage Alerts */}
        {!loading && (
          <div className="space-y-3">
            <UsageLimitAlert type="customers" currentUsage={stats.totalCustomers} />
            <UsageLimitAlert type="stamps" currentUsage={stats.stampsToday} />
          </div>
        )}
        
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Admin Dashboard</h2>
            <p className="text-muted-foreground">
              {tenantId ? 'Verwalten Sie Ihre Kunden und Treueprogramme' : 'Erstellen Sie Ihr erstes Unternehmen im Superadmin-Bereich'}
            </p>
          </div>
          
          <div className="flex gap-3">
            {!tenantId ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Setup erforderlich:</strong> Sie benötigen ein Unternehmen, um Kunden zu verwalten. 
                  Kontaktieren Sie einen Superadmin oder lassen Sie sich ein Unternehmen erstellen.
                </p>
              </div>
            ) : (
              <>
                {/* TEMPORARILY HIDDEN: QR-Code Button - Code kept for future use */}
                {/* <Button variant="outline">
                  <QrCode className="mr-2 h-4 w-4" />
                  QR-Code
                </Button> */}
                <Button disabled={!canAddCustomers(stats.totalCustomers)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Neuer Kunde
                  {!canAddCustomers(stats.totalCustomers) && ' (Limit erreicht)'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Kunden gesamt
              </CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCustomers}</div>
              <p className="text-xs text-muted-foreground">
                {stats.activeCustomers} aktiv (30 Tage)
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Stempel gesamt
              </CardTitle>
              <Award className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalStamps}</div>
              <p className="text-xs text-muted-foreground">
                Alle Stempel vergeben
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Heute vergeben
              </CardTitle>
              <Calendar className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.stampsToday}</div>
              <p className="text-xs text-muted-foreground">
                Stempel heute
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Durchschnitt
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.totalCustomers > 0 ? (stats.totalStamps / stats.totalCustomers).toFixed(1) : '0.0'}
              </div>
              <p className="text-xs text-muted-foreground">
                Stempel pro Kunde
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Widget Grid */}
        {tenantId && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <FollowUpWidget tenantId={tenantId} />
            <RedemptionTasksWidget />
            <SocialEngagementWidget tenantId={tenantId} />
          </div>
        )}

        {/* Recent Activity and Quick Actions */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Letzte Aktivitäten</CardTitle>
              <CardDescription>
                Die neuesten Stempel-Vergaben
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Lade Aktivitäten...
                </div>
              ) : !tenantId ? (
                <div className="text-center py-8 text-muted-foreground">
                  <div className="mb-4">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold mb-2">Kein Unternehmen zugewiesen</h3>
                  <p className="text-sm">
                    Lassen Sie sich von einem Superadmin ein Unternehmen erstellen, um Kunden zu verwalten.
                  </p>
                </div>
              ) : recentActivity.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Noch keine Aktivitäten
                </div>
              ) : (
                <div className="space-y-4">
                  {recentActivity.slice(0, 6).map((activity) => (
                    <div key={activity.id} className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium">{activity.customer_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {activity.action} • {formatDate(activity.timestamp)}
                        </p>
                      </div>
                      <Badge variant="secondary" className="ml-2">
                        +{activity.points}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Schnellaktionen</CardTitle>
              <CardDescription>
                Häufig verwendete Funktionen
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                <Button variant="outline" className="justify-start h-12" onClick={() => navigate('/admin/unterstuetzer')}>
                  <Users className="mr-2 h-4 w-4" />
                  <div className="text-left">
                    <div className="font-medium">Unterstützer verwalten</div>
                    <div className="text-xs text-muted-foreground">Unterstützer anzeigen und bearbeiten</div>
                  </div>
                </Button>
                
                <Button variant="outline" className="justify-start h-12" onClick={() => navigate('/admin/qr-codes')}>
                  <QrCode className="mr-2 h-4 w-4" />
                  <div className="text-left">
                    <div className="font-medium">QR-Code scannen</div>
                    <div className="text-xs text-muted-foreground">Stempel vergeben</div>
                  </div>
                </Button>
                
                <Button variant="outline" className="justify-start h-12" onClick={() => navigate('/admin/belohnungen')}>
                  <Award className="mr-2 h-4 w-4" />
                  <div className="text-left">
                    <div className="font-medium">Prämien</div>
                    <div className="text-xs text-muted-foreground">Prämien konfigurieren</div>
                  </div>
                </Button>
                
                <Button variant="outline" className="justify-start h-12" onClick={() => navigate('/admin/analytik')}>
                  <TrendingUp className="mr-2 h-4 w-4" />
                  <div className="text-left">
                    <div className="font-medium">Berichte</div>
                    <div className="text-xs text-muted-foreground">Statistiken ansehen</div>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Performance Insights */}
        <Card>
          <CardHeader>
            <CardTitle>Performance Insights</CardTitle>
            <CardDescription>
              Wichtige Kennzahlen für Ihr Treueprogramm
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Kundenbindung</span>
                  <Badge variant="secondary">
                    {stats.totalCustomers > 0 ? Math.round((stats.activeCustomers / stats.totalCustomers) * 100) : 0}%
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Aktive Kunden in den letzten 30 Tagen
                </p>
              </div>
              
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Stempel-Rate</span>
                  <Badge variant="secondary">
                    {stats.stampsToday}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Stempel heute vergeben
                </p>
              </div>
              
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Engagement</span>
                  <Badge variant={stats.totalStamps > 50 ? "default" : "secondary"}>
                    {stats.totalStamps > 50 ? 'Hoch' : stats.totalStamps > 10 ? 'Mittel' : 'Niedrig'}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Basierend auf Gesamtaktivität
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Push Notifications - Only show if tenant exists */}
        {tenantId && (
          <PushNotificationManager 
            customers={customers}
            tenantId={tenantId}
          />
        )}

        {/* TEMPORARILY HIDDEN: System-Benachrichtigungen Card - Code kept for future use */}
        {/* <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              System-Benachrichtigungen
            </CardTitle>
            <CardDescription>
              Senden Sie wichtige Ankündigungen an alle Administratoren
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Für erweiterte systemweite Push-Benachrichtigungen besuchen Sie die{' '}
              <strong>Superadmin-Einstellungen</strong>.
            </p>
          </CardContent>
        </Card> */}

        {/* TEMPORARILY HIDDEN: Sicherheitsüberwachung Card - Code kept for future use */}
        {/* {tenantId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Sicherheitsüberwachung
              </CardTitle>
              <CardDescription>
                Überwachen Sie Sicherheitsereignisse und verdächtige Aktivitäten
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SecurityMonitoring />
            </CardContent>
          </Card>
        )} */}
      </div>
    </DashboardLayout>
  )
}

export default AdminDashboard